import { useState, useEffect } from "react";
import PanelRefreshButton from "./PanelRefreshButton";
import { API, axios } from "@/api";
import { Loader2, ArrowUpCircle, Ban, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function StagingQueuePanel() {
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const [sumRes, recRes] = await Promise.all([
        axios.get(`${API}/admin/staging/summary`),
        axios.get(`${API}/admin/staging/records?limit=50`),
      ]);
      setSummary(sumRes.data);
      setRecords(recRes.data.records || []);
      setLastRefreshedAt(Date.now());
    } catch { setSummary(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const handlePromote = async (bid, name) => {
    setPromoting(bid);
    try {
      await axios.post(`${API}/admin/staging/promote/${bid}`);
      toast.success(`Promoted "${name}" to active`);
      fetch();
    } catch { toast.error("Promote failed"); }
    finally { setPromoting(null); }
  };

  const handleSuspend = async (bid, name) => {
    setPromoting(bid);
    try {
      await axios.post(`${API}/admin/staging/suspend/${bid}?reason=manually_suspended`);
      toast.success(`Suspended "${name}"`);
      fetch();
    } catch { toast.error("Suspend failed"); }
    finally { setPromoting(null); }
  };

  const handleEvaluateAll = async () => {
    setEvaluating(true);
    try {
      const res = await axios.post(`${API}/admin/staging/evaluate-all`);
      const { promoted, suspended } = res.data;
      toast.success(`Evaluated: ${promoted} promoted, ${suspended} suspended`);
      fetch();
    } catch { toast.error("Evaluation failed"); }
    finally { setEvaluating(false); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>;

  return (
    <div className="space-y-4" data-testid="staging-queue-panel">
      <div className="flex justify-end -mb-2"><PanelRefreshButton onRefresh={fetch} lastRefreshedAt={lastRefreshedAt} testId="staging-refresh" /></div>
      {/* Stage Counts */}
      {summary && (
        <div className="grid grid-cols-3 gap-3" data-testid="staging-summary">
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-center">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-emerald-700" data-testid="active-count">{summary.active}</p>
            <p className="text-[10px] uppercase tracking-wider text-emerald-500 font-semibold">Active</p>
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-center">
            <AlertTriangle className="w-4 h-4 text-amber-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-amber-700" data-testid="staging-count">{summary.staging}</p>
            <p className="text-[10px] uppercase tracking-wider text-amber-500 font-semibold">Staging</p>
          </div>
          <div className="rounded-xl bg-slate-100 border border-slate-200 p-3 text-center">
            <Ban className="w-4 h-4 text-slate-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-slate-600" data-testid="suspended-count">{summary.suspended}</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Suspended</p>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white text-xs font-medium rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50"
          onClick={handleEvaluateAll}
          disabled={evaluating}
          data-testid="promote-all-btn"
        >
          {evaluating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Promote All Eligible
        </button>
      </div>

      {/* Staging records list */}
      {records.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4" data-testid="staging-empty">No records in staging</p>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="staging-records-list">
          <p className="text-xs text-slate-500 font-semibold">Top {records.length} Staging Records (by completeness)</p>
          {records.map((r) => (
            <div
              key={r.business_id}
              className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100"
              data-testid={`staging-record-${r.business_id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700 truncate">{r.name || "Unnamed"}</p>
                <p className="text-[11px] text-slate-400">{r.city}, {r.state} &middot; Score: {r.completeness_score ?? 0}</p>
                {r.staging_reason && (
                  <p className="text-[11px] text-red-500 font-medium mt-0.5" data-testid={`staging-reason-${r.business_id}`}>
                    {r.staging_reason}
                  </p>
                )}
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button
                  className="p-1.5 rounded-lg bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors disabled:opacity-40"
                  onClick={() => handlePromote(r.business_id, r.name)}
                  disabled={promoting === r.business_id}
                  title="Promote to active"
                  data-testid={`promote-btn-${r.business_id}`}
                >
                  {promoting === r.business_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowUpCircle className="w-3.5 h-3.5" />}
                </button>
                <button
                  className="p-1.5 rounded-lg bg-red-100 text-red-500 hover:bg-red-200 transition-colors disabled:opacity-40"
                  onClick={() => handleSuspend(r.business_id, r.name)}
                  disabled={promoting === r.business_id}
                  title="Suspend"
                  data-testid={`suspend-btn-${r.business_id}`}
                >
                  <Ban className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
